import React from 'react';
import GroupChildCard from './GroupChildCard';
import * as styles from './GroupUserCard.module.scss';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import AvatarStack from '../components/AvatarStack';
import Badge from '../components/Badge';
import { MenuItem } from '../components/Menu';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { GroupUserRole } from '../graphql/generated/graphql';
import { UIActions } from '../ui/uiSlice';
import AdminBadge from '../user/AdminBadge';
import UserBadge from '../user/profile/UserBadge';
import { useGetUser } from '../user/useAllUsers';
import userPath from '../utils/url/userPath';

const GROUP_USER_CARD_FRAGMENT = gql(`
  fragment GroupUserCard_member on User {
    id
    ...useGetUser_user
  }
`);

interface GroupUserCardProps {
  user: DocumentType<typeof GROUP_USER_CARD_FRAGMENT>;
  owner?: boolean;
  onChangeRole?: (role: GroupUserRole) => Promise<unknown>;
  onRemove?: () => Promise<unknown>;
}

const GroupUserCard = ({
  user,
  owner = false,
  onChangeRole,
  onRemove,
}: GroupUserCardProps) => {
  const dispatch = useAppDispatch();
  const getUser = useGetUser();
  const fullUser = getUser(user);
  const { name, title } = fullUser;
  const isSelf = useAppSelector((state) => state.session.user.id === user.id);

  const showToast = (message: string) => {
    dispatch(
      UIActions.pushToast({
        level: 'success',
        message,
      }),
    );
  };

  return (
    <GroupChildCard
      to={userPath(fullUser)}
      avatars={<AvatarStack items={[fullUser]} />}
      badge={owner && <Badge>Owner</Badge>}
      title={
        <div className={styles.title}>
          <div className={styles.name}>{name}</div>
          {fullUser.type && ['GUEST', 'BOT'].includes(fullUser.type) && (
            <UserBadge user={fullUser} inline={false} />
          )}
        </div>
      }
      description={title}
      menuItems={
        (onChangeRole || onRemove) && (
          <>
            {onChangeRole &&
              (owner ? (
                <MenuItem
                  onClick={() =>
                    onChangeRole(GroupUserRole.Member).then(() =>
                      showToast(
                        `${name} has been demoted to a standard member.`,
                      ),
                    )
                  }
                >
                  {isSelf ? 'Demote yourself' : 'Demote to member'}
                </MenuItem>
              ) : (
                <MenuItem
                  onClick={() =>
                    onChangeRole(GroupUserRole.Owner).then(() =>
                      showToast(`${name} has been promoted to an owner.`),
                    )
                  }
                  right={!owner && isSelf ? <AdminBadge /> : undefined}
                >
                  {isSelf ? 'Become owner' : 'Promote to owner'}
                </MenuItem>
              ))}
            {onRemove && (
              <MenuItem
                danger
                onClick={() =>
                  onRemove().then(() =>
                    showToast(`${name} has been removed from the group.`),
                  )
                }
              >
                {isSelf ? 'Leave group' : 'Remove from group'}
              </MenuItem>
            )}
          </>
        )
      }
    />
  );
};

export default GroupUserCard;
